import bcrypt from 'bcryptjs'
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { generateKeyPair, importPrivateKey, normalizeNickname } from '../crypto/ZionCipher.ts'
import {
  createMasterPasswordKdfRecord,
  deriveAesGcmKey,
  parseMasterPasswordKdf,
  PBKDF2_ITERATIONS,
  PBKDF2_SALT_BYTES,
  serializeMasterPasswordKdf,
  verifyMasterPasswordKdf,
  type MasterPasswordKdfRecord,
} from '../crypto/pbkdf2Kdf.ts'
import { base64ToArrayBuffer, bytesToBase64 } from '../crypto/base64.ts'

interface ZionDb extends DBSchema {
  meta: {
    key: string
    value: string
  }
  contactKeys: {
    key: string
    value: string
  }
}

const DB_NAME = 'zion-web'
const DB_VERSION = 1

const KEY_PRIVATE = 'private_key'
const KEY_PUBLIC = 'public_key'
const KEY_MASTER_KDF = 'master_password_kdf'
/** Legacy bcrypt — migrato automaticamente al primo unlock riuscito. */
const KEY_MASTER_HASH = 'master_password_hash'
const KEY_NICK_BASE = 'nick_base'
const KEY_NICK_NUMBER = 'nick_number'
const KEY_NICK_DATE = 'nick_date'
/** Vault AES-GCM: identity locale cifrata con master password. */
const KEY_IDENTITY_VAULT = 'identity_vault_v1'

const VAULT_IV_LEN = 12

interface IdentityVaultPayload {
  v: 1
  privateKeyBase64: string
  publicKeyBase64: string
  nickBase: string | null
  nickNumber: number
  contactKeys: Record<string, string>
}

let dbPromise: Promise<IDBPDatabase<ZionDb>> | null = null

function getDb(): Promise<IDBPDatabase<ZionDb>> {
  if (!dbPromise) {
    dbPromise = openDB<ZionDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta')
        if (!db.objectStoreNames.contains('contactKeys')) db.createObjectStore('contactKeys')
      },
    })
  }
  return dbPromise
}

export async function ensureKeyPair(): Promise<{ publicKeyBase64: string; privateKey: CryptoKey }> {
  const db = await getDb()
  let publicKey = await db.get('meta', KEY_PUBLIC)
  let privateB64 = await db.get('meta', KEY_PRIVATE)

  if (!publicKey || !privateB64) {
    const pair = await generateKeyPair()
    await db.put('meta', pair.publicKeyBase64, KEY_PUBLIC)
    await db.put('meta', pair.privateKeyBase64, KEY_PRIVATE)
    return { publicKeyBase64: pair.publicKeyBase64, privateKey: pair.privateKey }
  }

  return {
    publicKeyBase64: publicKey,
    privateKey: await importPrivateKey(privateB64),
  }
}

export async function getPublicKeyBase64(): Promise<string> {
  const db = await getDb()
  return (await db.get('meta', KEY_PUBLIC)) ?? ''
}

export async function getPrivateKeyBase64(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', KEY_PRIVATE)) ?? null
}

export async function getPrivateKey(): Promise<CryptoKey> {
  const { privateKey } = await ensureKeyPair()
  return privateKey
}

async function storeMasterPasswordKdf(record: MasterPasswordKdfRecord): Promise<void> {
  const db = await getDb()
  await db.put('meta', serializeMasterPasswordKdf(record), KEY_MASTER_KDF)
  await db.delete('meta', KEY_MASTER_HASH)
}

export async function getMasterPasswordKdfRecord(): Promise<MasterPasswordKdfRecord | null> {
  const db = await getDb()
  const raw = await db.get('meta', KEY_MASTER_KDF)
  return parseMasterPasswordKdf(raw)
}

/** Legacy bcrypt export — solo per backup importati da versioni precedenti. */
export async function getMasterPasswordHash(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', KEY_MASTER_HASH)) ?? null
}

export async function setMasterPasswordHash(plainPassword: string): Promise<void> {
  const record = await createMasterPasswordKdfRecord(plainPassword)
  await storeMasterPasswordKdf(record)
}

async function readAllContactKeys(): Promise<Record<string, string>> {
  const db = await getDb()
  const keys = await db.getAllKeys('contactKeys')
  const out: Record<string, string> = {}
  for (const k of keys) {
    const v = await db.get('contactKeys', k)
    if (typeof k === 'string' && v?.trim()) out[k] = v.trim()
  }
  return out
}

async function writeContactKeys(map: Record<string, string>): Promise<void> {
  // Solo per restore vault completo: merge atomico per chiave, senza wipe globale
  // che potrebbe cancellare chiavi scritte in parallelo da altri contatti.
  const db = await getDb()
  const tx = db.transaction('contactKeys', 'readwrite')
  for (const [k, v] of Object.entries(map)) {
    if (k && v?.trim()) await tx.store.put(v.trim(), k)
  }
  await tx.done
}

async function buildIdentityVaultPayload(): Promise<IdentityVaultPayload> {
  const db = await getDb()
  const privateKeyBase64 = (await db.get('meta', KEY_PRIVATE))?.trim() ?? ''
  const publicKeyBase64 = (await db.get('meta', KEY_PUBLIC))?.trim() ?? ''
  if (!privateKeyBase64 || !publicKeyBase64) throw new Error('identity_incomplete')
  const nickRaw = await db.get('meta', KEY_NICK_NUMBER)
  return {
    v: 1,
    privateKeyBase64,
    publicKeyBase64,
    nickBase: (await db.get('meta', KEY_NICK_BASE)) ?? null,
    nickNumber: nickRaw ? parseInt(nickRaw, 10) || 0 : 0,
    contactKeys: await readAllContactKeys(),
  }
}

async function encryptIdentityVault(payload: IdentityVaultPayload, password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES))
  const iv = crypto.getRandomValues(new Uint8Array(VAULT_IV_LEN))
  const key = await deriveAesGcmKey(password, salt, PBKDF2_ITERATIONS)
  const plain = new TextEncoder().encode(JSON.stringify(payload))
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain),
  )
  const out = new Uint8Array(salt.length + iv.length + ciphertext.length)
  out.set(salt, 0)
  out.set(iv, salt.length)
  out.set(ciphertext, salt.length + iv.length)
  return bytesToBase64(out)
}

async function decryptIdentityVault(wrappedB64: string, password: string): Promise<IdentityVaultPayload> {
  const data = new Uint8Array(base64ToArrayBuffer(wrappedB64))
  if (data.length < PBKDF2_SALT_BYTES + VAULT_IV_LEN + 16) throw new Error('vault_corrupt')
  const salt = data.slice(0, PBKDF2_SALT_BYTES)
  const iv = data.slice(PBKDF2_SALT_BYTES, PBKDF2_SALT_BYTES + VAULT_IV_LEN)
  const ciphertext = data.slice(PBKDF2_SALT_BYTES + VAULT_IV_LEN)
  const key = await deriveAesGcmKey(password, salt, PBKDF2_ITERATIONS)
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource,
  )
  const parsed = JSON.parse(new TextDecoder().decode(plain)) as IdentityVaultPayload
  if (parsed?.v !== 1 || !parsed.privateKeyBase64?.trim() || !parsed.publicKeyBase64?.trim()) {
    throw new Error('vault_invalid')
  }
  return parsed
}

async function applyIdentityVaultPayload(payload: IdentityVaultPayload): Promise<void> {
  const db = await getDb()
  await db.put('meta', payload.privateKeyBase64.trim(), KEY_PRIVATE)
  await db.put('meta', payload.publicKeyBase64.trim(), KEY_PUBLIC)
  if (payload.nickBase?.trim()) await db.put('meta', payload.nickBase.trim(), KEY_NICK_BASE)
  if (payload.nickNumber > 0) await db.put('meta', String(payload.nickNumber), KEY_NICK_NUMBER)
  if (payload.contactKeys && typeof payload.contactKeys === 'object') {
    await writeContactKeys(payload.contactKeys)
  }
}

/** Dopo unlock: se esiste vault, ri-applica chiavi (verifica password già fatta). */
export async function unlockIdentityVault(plainPassword: string): Promise<void> {
  const db = await getDb()
  const wrapped = await db.get('meta', KEY_IDENTITY_VAULT)
  if (!wrapped?.trim()) return
  const payload = await decryptIdentityVault(wrapped, plainPassword)
  await applyIdentityVaultPayload(payload)
}

/**
 * Cifra (o ri-cifra) il vault identità con la password data, senza cambiare il KDF.
 * Usato al primo avvio dopo setMasterPasswordHash.
 */
export async function sealIdentityVault(password: string): Promise<void> {
  if (password.length < 8) throw new Error('password_too_short')
  const payload = await buildIdentityVaultPayload()
  const wrapped = await encryptIdentityVault(payload, password)
  const db = await getDb()
  await db.put('meta', wrapped, KEY_IDENTITY_VAULT)
  await applyIdentityVaultPayload(payload)
}

/**
 * Cambia master password: verifica attuale, ri-cifra vault identità (chiavi + contatti),
 * aggiorna KDF.
 */
export async function changeMasterPassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  const { validateNewPassword } = await import('../crypto/passwordValidation.ts')
  const check = validateNewPassword(newPassword, confirmPassword)
  if (!check.ok) {
    if (check.reason === 'mismatch') throw new Error('password_mismatch')
    throw new Error('password_too_short')
  }
  if (!(await verifyMasterPassword(currentPassword))) throw new Error('password_wrong')

  const db = await getDb()
  const existingVault = await db.get('meta', KEY_IDENTITY_VAULT)
  let payload: IdentityVaultPayload
  if (existingVault?.trim()) {
    try {
      payload = await decryptIdentityVault(existingVault, currentPassword)
      await applyIdentityVaultPayload(payload)
    } catch {
      payload = await buildIdentityVaultPayload()
    }
  } else {
    payload = await buildIdentityVaultPayload()
  }

  const wrapped = await encryptIdentityVault(payload, newPassword)
  await db.put('meta', wrapped, KEY_IDENTITY_VAULT)
  await applyIdentityVaultPayload(payload)
  await setMasterPasswordHash(newPassword)
}

export async function verifyMasterPassword(plainPassword: string): Promise<boolean> {
  const kdf = await getMasterPasswordKdfRecord()
  if (kdf) {
    return verifyMasterPasswordKdf(plainPassword, kdf)
  }

  const legacyHash = await getMasterPasswordHash()
  if (!legacyHash) return false
  const ok = await bcrypt.compare(plainPassword, legacyHash)
  if (ok) {
    await setMasterPasswordHash(plainPassword)
  }
  return ok
}

export async function hasMasterPassword(): Promise<boolean> {
  const db = await getDb()
  const kdf = await db.get('meta', KEY_MASTER_KDF)
  if (kdf?.trim()) return true
  return !!(await db.get('meta', KEY_MASTER_HASH))
}

export async function setNickIdentity(base: string, number: number, dateUtc: string): Promise<void> {
  const db = await getDb()
  await db.put('meta', base, KEY_NICK_BASE)
  await db.put('meta', String(number), KEY_NICK_NUMBER)
  await db.put('meta', dateUtc, KEY_NICK_DATE)
}

export async function getNickBase(): Promise<string | null> {
  const db = await getDb()
  return (await db.get('meta', KEY_NICK_BASE)) ?? null
}

export async function getNickNumber(): Promise<number> {
  const db = await getDb()
  const raw = await db.get('meta', KEY_NICK_NUMBER)
  return raw ? parseInt(raw, 10) : 0
}

export async function hasNickIdentity(): Promise<boolean> {
  const base = await getNickBase()
  const num = await getNickNumber()
  return !!(base?.trim() && num > 0)
}

export async function saveContactPublicKey(nickname: string, publicKey: string): Promise<void> {
  await saveContactPublicKeyBackup(nickname, '', publicKey)
}

export async function getContactPublicKey(nickname: string): Promise<string | null> {
  return getContactPublicKeyBackup(nickname)
}

const SESS_KEY_PREFIX = 'sess_'

/** Backup persistente chiavi contatto (nickname + sessionToken) in IndexedDB. */
export async function saveContactPublicKeyBackup(
  nickname: string,
  sessionToken: string,
  publicKey: string,
): Promise<void> {
  const key = publicKey.trim()
  if (!key) return
  const db = await getDb()
  const nick = normalizeNickname(nickname)
  if (nick) await db.put('contactKeys', key, `key_${nick}`)
  const sess = sessionToken.trim().toLowerCase()
  if (sess) await db.put('contactKeys', key, `${SESS_KEY_PREFIX}${sess}`)
}

export async function getContactPublicKeyBackup(
  nickname: string,
  sessionToken?: string,
): Promise<string | null> {
  const db = await getDb()
  const nick = normalizeNickname(nickname)
  if (nick) {
    const byNick = await db.get('contactKeys', `key_${nick}`)
    if (byNick?.trim()) return byNick.trim()
  }
  const sess = sessionToken?.trim().toLowerCase()
  if (sess) {
    const bySess = await db.get('contactKeys', `${SESS_KEY_PREFIX}${sess}`)
    if (bySess?.trim()) return bySess.trim()
  }
  return null
}

export async function importIdentityFromBackup(input: {
  privateKeyBase64: string
  publicKeyBase64: string
  nickBase?: string
  nickNumber?: number
  masterPasswordKdf?: string
  masterPasswordHash?: string
}): Promise<void> {
  const db = await getDb()
  await db.put('meta', input.privateKeyBase64.trim(), KEY_PRIVATE)
  await db.put('meta', input.publicKeyBase64.trim(), KEY_PUBLIC)
  if (input.nickBase?.trim()) await db.put('meta', input.nickBase.trim(), KEY_NICK_BASE)
  if (input.nickNumber && input.nickNumber > 0) {
    await db.put('meta', String(input.nickNumber), KEY_NICK_NUMBER)
  }

  const kdf = parseMasterPasswordKdf(input.masterPasswordKdf)
  if (kdf) {
    await storeMasterPasswordKdf(kdf)
  } else if (input.masterPasswordHash?.trim()) {
    await db.put('meta', input.masterPasswordHash.trim(), KEY_MASTER_HASH)
    await db.delete('meta', KEY_MASTER_KDF)
  }

  const today = new Date().toISOString().slice(0, 10)
  await db.put('meta', today, KEY_NICK_DATE)
}

export async function clearAllKeys(): Promise<void> {
  const db = await getDb()
  await db.clear('meta')
  await db.clear('contactKeys')
}
